// React imports
import { useState, useMemo } from 'react';
import { useNavigate, useParams, useOutletContext, useLocation } from 'react-router-dom';

// Generic imports
import queryString from 'query-string';
import axios from 'axios';
import _ from 'lodash';

// Mui imports
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';

// Local imports
import OpportunityOneOffScreen from './OpportunityOneOffScreen.js';
import OpportunityOneOffEmailScreen from './OpportunityOneOffEmailScreen.js';
import OpportunityOneOffFreeTextScreen from './freetext/OpportunityOneOffFreeTextScreen.js';
import OpportunityContractScreen from './OpportunityContractScreen.js';
import CreateTemplateDialog from '../components/CreateTemplateDialog.js';
import { migrateOpportunity } from '../utils/opportunity.js';

function OpportunityRouter(props) {
    const { me, org, accesstoken, opportunities, accounts, setAccounts, 
        seaPorts, unlocode, suppliers, onUpdateOpportunity, minimizedSearch,
        setMinimizedSearch, onOrgUpdated, exchangeRates, featureToggle, 
        emailLoginInfo, onEmailRefreshConnection, onEmailLogout
    } = useOutletContext();
    const [ showDuplicationModal, setShowDuplicationModal ] = useState({});
    const [ showCreateTemplate, setShowCreateTemplate ] = useState(null);
    const [ refresh, setRefresh ] = useState(0);
    const { opportunityId } = useParams();
    const location = useLocation();

    const { forceStep } = queryString.parse(location.search);

    const possibleTags = useMemo(() => {
        return _(opportunities)
            .map(a => a.tags)
            .flatten()
            .uniqBy(a => a?.label)
            .compact()
            .sortBy(a => a?.label)
            .value();
    }, [opportunities]);

    if (!opportunities) return null;

    const opportunity = opportunities.find(op => op.id === opportunityId) || null;

    async function onDuplicate() {
        const o = _.cloneDeep(opportunity);

        setShowDuplicationModal({ open: true, message: 'Please wait while we duplicate your opportunity. We promise this won\'t take long.', showButton: null });

        // A duplicate starts fresh — drop state tags carried over from a won / closed /
        // already-sent original so the copy isn't pre-marked as won, closed, or sent to TMS.
        const STRIP_TAGS = ['Pipeline: Closed', 'Booking: Quote', 'Sent to TMS'];
        if (o.tags) o.tags = o.tags.filter(t => !STRIP_TAGS.includes(t?.label));

        // Repair any dirty buying-rate references before POSTing. A duplicate sends the whole
        // opportunity, which the strict create schema rejects if a field's buyingRates ref is
        // missing opportunityProductId or points at a rate that no longer exists (legacy /
        // freshly template-loaded state). migrateOpportunity backfills or drops such refs.
        migrateOpportunity({ opportunity: o });

        // Record the provenance of the copy in its internal Updates & Comments timeline, with a
        // link back to the original. Uses only schema-allowed update fields (created/createdBy/html)
        // so the create POST validates; createdBy must be a valid email, so skip if we don't have one.
        if (me?.email) {
            const originalRef = opportunity.readableId || opportunity.id;
            const originalLink = `/organization/${org.alias}/opportunities/${opportunity.id}`;
            const duplicationEntry = {
                created: new Date().getTime(),
                createdBy: me.email,
                html: `Created by duplicating opportunity <a href="${originalLink}">#${originalRef}</a>.`,
            };
            o.updates = [...(o.updates || []), duplicationEntry];
        }

        const FIELDS_TO_OMIT = ['status', 'parent', 'id', 'readableId', 'created', 'createdBy', 'owner', 'organizationId', 'log', 'modified', 'source', 'selected', 'exchangeRates', 'firstWonDate', 'lostReason', 'lostOffer'];
        try {
            const { data: newOpportunity } = await axios.post(`/api/opportunities/?accesstoken=${accesstoken}&organizationId=${org.id}`, {
                opportunity: _.omit(o, FIELDS_TO_OMIT)
            });

            onUpdateOpportunity({ opportunity: newOpportunity });

            setShowDuplicationModal({ open: true, message: 'Opportunity duplicated successfully.', showButton: newOpportunity.id, type: newOpportunity.type });
        } catch (err) {
            // Never let a failed duplicate take the whole page down — surface the error in the modal.
            console.error('[onDuplicate] failed to duplicate opportunity', err?.response?.data || err);
            setShowDuplicationModal({ open: true, error: true, message: 'We couldn\'t duplicate this opportunity. Please try again, and contact support if the problem persists.', showButton: null });
        }
    }

    function onCreateTemplate({ offer }) {
        setShowCreateTemplate({ offer });
    }

    if (!accounts || !exchangeRates) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (opportunityId === 'new' || opportunity ) {
        if (opportunity?.type === 'one-off') {
            return (
                <>
                    <OpportunityOneOffScreen 
                        key={`${refresh}-${opportunityId}`}
                        {...props} 
                        accounts={accounts} 
                        setAccounts={setAccounts} 
                        onUpdateOpportunity={onUpdateOpportunity}
                        seaPorts={seaPorts} 
                        unlocode={unlocode}
                        suppliers={suppliers} 
                        opportunity={opportunity} 
                        accesstoken={accesstoken} 
                        org={org} 
                        minimizedSearch={minimizedSearch}
                        setMinimizedSearch={setMinimizedSearch}
                        me={me}
                        onDuplicate={onDuplicate}
                        forceStep={forceStep}
                        onOrgUpdated={onOrgUpdated}
                        onCreateTemplate={onCreateTemplate}
                        possibleTags={possibleTags}
                        exchangeRates={exchangeRates}
                        contracts={opportunities.filter(o => o.type === 'contract')}
                        featureToggle={featureToggle}
                        emailLoginInfo={emailLoginInfo}
                        onEmailRefreshConnection={onEmailRefreshConnection}
                        onEmailLogout={onEmailLogout}
                        opportunities={opportunities}
                    />
                    <DuplicateModal showDuplicationModal={showDuplicationModal} setShowDuplicationModal={setShowDuplicationModal} setRefresh={setRefresh} />
                    {showCreateTemplate && (
                        <CreateTemplateDialog
                            org={org}
                            opportunity={opportunity}
                            accesstoken={accesstoken}
                            onOrgUpdated={onOrgUpdated}
                            offer={showCreateTemplate.offer} 
                            onClose={() => setShowCreateTemplate(null)}
                        />
                    )}
                </>
            );
        } else if (opportunity?.type === 'one-off-email') {
            return (
                <>
                    <OpportunityOneOffEmailScreen 
                        key={`${refresh}-${opportunityId}`}
                        {...props} 
                        accounts={accounts} 
                        setAccounts={setAccounts} 
                        onUpdateOpportunity={onUpdateOpportunity}
                        seaPorts={seaPorts} 
                        unlocode={unlocode}
                        suppliers={suppliers} 
                        opportunity={opportunity} 
                        accesstoken={accesstoken} 
                        org={org} 
                        minimizedSearch={minimizedSearch}
                        setMinimizedSearch={setMinimizedSearch}
                        me={me}
                        onDuplicate={onDuplicate}
                        forceStep={forceStep}
                        onOrgUpdated={onOrgUpdated}
                        onCreateTemplate={onCreateTemplate}
                        possibleTags={possibleTags}
                        exchangeRates={exchangeRates}
                        contracts={opportunities.filter(o => o.type === 'contract')}
                        featureToggle={featureToggle}
                        emailLoginInfo={emailLoginInfo}
                        onEmailRefreshConnection={onEmailRefreshConnection}
                        onEmailLogout={onEmailLogout}
                        opportunities={opportunities}
                    />
                    <DuplicateModal 
                        showDuplicationModal={showDuplicationModal} 
                        setShowDuplicationModal={setShowDuplicationModal} 
                        setRefresh={setRefresh}
                    />
                    {showCreateTemplate && (
                        <CreateTemplateDialog
                            org={org}
                            opportunity={opportunity}
                            accesstoken={accesstoken}
                            onOrgUpdated={onOrgUpdated}
                            offer={showCreateTemplate.offer} 
                            onClose={() => setShowCreateTemplate(null)}
                        />
                    )}
                </>
            );
        } else if (opportunity?.type === 'one-off-free-text') {
            return (
                <>
                    <OpportunityOneOffFreeTextScreen
                        key={`${refresh}-${opportunityId}`}
                        {...props}
                        accounts={accounts}
                        setAccounts={setAccounts}
                        onUpdateOpportunity={onUpdateOpportunity}
                        seaPorts={seaPorts}
                        unlocode={unlocode}
                        suppliers={suppliers}
                        opportunity={opportunity}
                        accesstoken={accesstoken}
                        org={org}
                        minimizedSearch={minimizedSearch}
                        setMinimizedSearch={setMinimizedSearch}
                        me={me}
                        onDuplicate={onDuplicate}
                        forceStep={forceStep}
                        onOrgUpdated={onOrgUpdated}
                        onCreateTemplate={onCreateTemplate}
                        possibleTags={possibleTags}
                        exchangeRates={exchangeRates}
                        contracts={opportunities.filter(o => o.type === 'contract')}
                        featureToggle={featureToggle}
                        emailLoginInfo={emailLoginInfo}
                        onEmailRefreshConnection={onEmailRefreshConnection}
                        onEmailLogout={onEmailLogout}
                        opportunities={opportunities}
                    />
                    <DuplicateModal
                        showDuplicationModal={showDuplicationModal}
                        setShowDuplicationModal={setShowDuplicationModal}
                        setRefresh={setRefresh}
                    />
                </>
            );
        } else if (opportunity?.type === 'contract') {
            return (
                <>
                    <OpportunityContractScreen 
                        key={`${refresh}-${opportunityId}`}
                        {...props} 
                        accounts={accounts} 
                        setAccounts={setAccounts} 
                        onUpdateOpportunity={onUpdateOpportunity}
                        seaPorts={seaPorts} 
                        unlocode={unlocode}
                        opportunity={opportunity} 
                        accesstoken={accesstoken} 
                        org={org} 
                        minimizedSearch={minimizedSearch}
                        setMinimizedSearch={setMinimizedSearch}
                        onDuplicate={onDuplicate}
                        forceStep={forceStep}
                        me={me}
                        onCreateTemplate={onCreateTemplate}
                        suppliers={suppliers} 
                        possibleTags={possibleTags}
                        exchangeRates={exchangeRates}
                        emailLoginInfo={emailLoginInfo}
                        onEmailRefreshConnection={onEmailRefreshConnection}
                        onEmailLogout={onEmailLogout}
                    />
                    <DuplicateModal showDuplicationModal={showDuplicationModal} setShowDuplicationModal={setShowDuplicationModal} setRefresh={setRefresh} />
                    {showCreateTemplate && (
                        <CreateTemplateDialog
                            org={org}
                            opportunity={opportunity}
                            accesstoken={accesstoken}
                            onOrgUpdated={onOrgUpdated}
                            offer={showCreateTemplate.offer} 
                            onClose={() => setShowCreateTemplate(null)}
                        />
                    )}
                </>
            );
        }
    }


    return (
        <div>
            Unknown error
        </div>
    )

}

export default OpportunityRouter;

function DuplicateModal(props) {
    const { showDuplicationModal, setShowDuplicationModal, setRefresh } = props;
    const navigate = useNavigate();

    return (
        <Dialog open={!!showDuplicationModal.open} onClose={() => setShowDuplicationModal({...showDuplicationModal, open: false})}>
            <DialogTitle>
                Duplicating Opportunity
            </DialogTitle>
            <DialogContent>
                <DialogContentText>
                    {showDuplicationModal.message}
                </DialogContentText>
                    {!showDuplicationModal.showButton && !showDuplicationModal.error && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '24px' }}>
                            <CircularProgress />
                        </Box>
                    )}
            </DialogContent>
            <DialogActions>
                {showDuplicationModal.error ? (
                    <Button
                        variant='outlined'
                        onClick={() => setShowDuplicationModal({...showDuplicationModal, open: false})}
                        autoFocus
                    >
                        Close
                    </Button>
                ) : (
                    <Button
                        variant='contained'
                        disabled={!showDuplicationModal.showButton}
                        onClick={() => {
                            navigate(`../opportunities/${showDuplicationModal.showButton}?forceStep=1`);
                            setRefresh(r => r + 1);
                            setShowDuplicationModal({...showDuplicationModal, open: false});
                        }}
                        autoFocus
                    >
                        Go to Duplicated Shipment
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
